class Cache {

  constructor () {

  }

  /**
   * The account whose data this cache entry belongs to.
   *
   * One login can now look at several accounts, and the cache is read back
   * verbatim when the API times out — so a key that is only the request path
   * would serve one account's sensors to an offline session that has since
   * switched to another.
   *
   * `window.currentUser.account_id` is the account being viewed (the API resolves
   * it from the session's context); `.id` is their own login. The localStorage
   * copy is the fallback for the window before /authenticate resolves, which is
   * exactly when an offline read happens — without it the first read of a page
   * load would land in a different namespace than the write that follows it.
   */
  accountKey() {

    const user = window.currentUser;

    if(user && (user.account_id || user.id)) {
      return String(user.account_id || user.id);
    }

    return localStorage.getItem('account_id') || 'anon';

  }

  /**
   * Cache entries are namespaced per account. Keys written before this existed
   * live under `cache.<key>` and are simply never read again, which is the
   * correct outcome: they cannot be attributed to an account.
   *
   * `accountKey` names a namespace other than the one being viewed. The account
   * switcher uses it to seed the account it is switching *to* before the reload,
   * while `window.currentUser` still describes the account it is leaving.
   */
  key(key, accountKey = null) {
    return `cache.${accountKey || this.accountKey()}.${key}`;
  }

  get(key) {

    const value = localStorage.getItem(this.key(key));

    if(value) {
      return JSON.parse(value);
    } else {
      return null;
    }

  }

  /**
   *
   * @param key
   * @param value
   * @param accountKey  namespace to write into, defaulting to the account in view
   */
  save(key, value, accountKey = null) {

    try {
      const cacheItem = {
        timestamp: Date.now(),
        value,
      };

      localStorage.setItem(this.key(key, accountKey), JSON.stringify(cacheItem));

    } catch (e) {
      console.error("Error saving cache", e);
    }

  }

  remove(key) {
    localStorage.removeItem(this.key(key));
  }

  /**
   * Drop every cached response, for any account.
   *
   * Called when switching accounts: keying alone stops the wrong account's data
   * being *read*, but leaving it in localStorage keeps one account's sensor
   * history sitting in another's browser storage for no reason.
   */
  clearAll() {

    const keys = [];

    for(let i = 0; i < localStorage.length; i++) {

      const key = localStorage.key(i);

      if(key && key.startsWith('cache.')) {
        keys.push(key);
      }

    }

    keys.forEach(key => localStorage.removeItem(key));

  }

}

const CacheInstance = new Cache();

export default CacheInstance;
